import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Building2, Check, Copy, Globe, Loader2, AtSign,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import { detalleError } from './contexto';

const QUIEN = [
  { id: 'cualquiera', titulo: 'Cualquier persona con el enlace', desc: 'No hace falta cuenta para responder.', Icono: Globe },
  { id: 'organizacion', titulo: 'Solo personas de tu organización', desc: 'Tendrán que iniciar sesión en Orvian.', Icono: Building2 },
  { id: 'dominio', titulo: 'Solo cuentas de un dominio', desc: 'Iniciando sesión con un correo @dominio.', Icono: AtSign },
];

/**
 * Publicar el formulario (como el botón "Publicar" de Google Forms): mientras no se publica, nadie
 * puede responder, aunque tenga el enlace. Aquí se elige también quién puede responder.
 */
export default function DialogoPublicar({ open, onOpenChange, formulario, onPublicacion, correoUsuario }) {
  const pub = formulario?.publicacion || {};
  const [quien, setQuien] = useState(pub.quien || 'cualquiera');
  const [dominio, setDominio] = useState(pub.dominio || (correoUsuario || '').split('@')[1] || '');
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuien(pub.quien || 'cualquiera');
    setDominio(pub.dominio || (correoUsuario || '').split('@')[1] || '');
  }, [open, pub.quien, pub.dominio, correoUsuario]);

  const guardar = async (parcial, mensaje) => {
    setOcupado(true);
    try {
      await onPublicacion(parcial);
      if (mensaje) toast.success(mensaje);
      return true;
    } catch (err) {
      toast.error(detalleError(err, 'No se pudo guardar la publicación'));
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const publicar = async () => {
    if (quien === 'dominio' && !/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(dominio.trim())) {
      toast.error('Escribe un dominio válido, por ejemplo orvian.mx');
      return;
    }
    await guardar(
      { publicado: true, quien, dominio: quien === 'dominio' ? dominio.trim().toLowerCase() : null },
      pub.publicado ? 'Se guardó quién puede responder' : 'Formulario publicado',
    );
  };

  const copiar = async () => {
    if (!pub.url_publica) return;
    try {
      await navigator.clipboard.writeText(pub.url_publica);
      setCopiado(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      window.prompt('Copia el enlace:', pub.url_publica);
    }
  };

  const cambioQuien = quien !== (pub.quien || 'cualquiera') || (quien === 'dominio' && dominio !== (pub.dominio || ''));

  return (
    <Dialog open={open} onOpenChange={(v) => !ocupado && onOpenChange(v)}>
      <DialogContent className="fe-dialogo max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle>{pub.publicado ? 'Configuración de publicación' : 'Publicar formulario'}</DialogTitle>
          <DialogDescription>
            {pub.publicado
              ? 'El formulario está publicado. Puedes cambiar quién responde o dejar de publicarlo.'
              : 'Al publicarlo, quien tenga el enlace podrá responder. Los cambios que hagas después se ven al momento.'}
          </DialogDescription>
        </DialogHeader>

        <div className="fe-publicar-quien" role="radiogroup" aria-label="Quién puede responder">
          <div className="fe-config-rotulo">Quién puede responder</div>
          {QUIEN.map(({ id, titulo, desc, Icono }) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={quien === id}
              className={`fe-opcion-grande ${quien === id ? 'is-elegida' : ''}`}
              onClick={() => setQuien(id)}
              disabled={ocupado}
            >
              <Icono />
              <span>
                <strong>{titulo}</strong>
                <small>{desc}</small>
              </span>
              {quien === id && <Check className="fe-opcion-grande-check" />}
            </button>
          ))}
          {quien === 'dominio' && (
            <label className="fe-publicar-dominio">
              <span>@</span>
              <input className="fe-input-caja" value={dominio} onChange={(e) => setDominio(e.target.value)} placeholder="orvian.mx" aria-label="Dominio" />
            </label>
          )}
        </div>

        {pub.publicado && (
          <>
            <label className="fe-fila-ajuste" htmlFor="pub-acepta">
              <span className="fe-fila-ajuste-texto">
                <span className="fe-fila-ajuste-titulo">Aceptar respuestas</span>
                <span className="fe-fila-ajuste-desc">Apagado, el enlace muestra el mensaje de formulario cerrado.</span>
              </span>
              <Switch
                id="pub-acepta"
                checked={pub.acepta_respuestas !== false}
                disabled={ocupado}
                onCheckedChange={(v) => guardar({ acepta_respuestas: v }, v ? 'Acepta respuestas' : 'Ya no acepta respuestas')}
              />
            </label>
            {pub.url_publica && (
              <div className="fe-enlace-copiar">
                <input readOnly value={pub.url_publica} className="fe-input-caja" aria-label="Enlace para responder" onFocus={(e) => e.target.select()} />
                <button type="button" className="fe-btn-borde" onClick={copiar}>
                  {copiado ? <Check /> : <Copy />} Copiar
                </button>
              </div>
            )}
          </>
        )}

        <div className="fe-dialogo-pie">
          {pub.publicado && (
            <button
              type="button"
              className="fe-btn-texto fe-peligro"
              disabled={ocupado}
              onClick={async () => {
                if (await guardar({ publicado: false }, 'El formulario dejó de estar publicado')) onOpenChange(false);
              }}
            >
              Dejar de publicar
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className="fe-btn-borde" onClick={() => onOpenChange(false)} disabled={ocupado}>
            {pub.publicado && !cambioQuien ? 'Listo' : 'Cancelar'}
          </button>
          {(!pub.publicado || cambioQuien) && (
            <button type="button" className="fe-btn-primario" onClick={publicar} disabled={ocupado}>
              {ocupado && <Loader2 className="od-spin" />} {pub.publicado ? 'Guardar' : 'Publicar'}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
